import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { streamCompanion } from "@/lib/companion";
import { api } from "@/lib/api";
import { softChime } from "@/lib/push";

const RemansoContext = createContext(null);
const TYPING_IDLE_MS = 5000;
const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;

// Binding comes from the open entry editor: { id, companion, route, body, getDraft, flush, select, onRoute, onCompanion, persistCompanion }
export const RemansoProvider = ({ children }) => {
  const [mode, setMode] = useState("escucha");
  const [panelOpen, setPanelOpen] = useState(false);
  const [binding, setBinding] = useState(null);
  const [thread, setThread] = useState([]);
  const [draftBody, setDraftBody] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [live, setLive] = useState(null);
  const [error, setError] = useState("");
  const [route, setRoute] = useState("libre");
  const [after, setAfter] = useState(null);
  const [config, setConfig] = useState(null);
  const [moods, setMoods] = useState([]);
  const [info, setInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const settingsRef = useRef(null);
  settingsRef.current = settings;
  const bindingRef = useRef(null);
  const threadRef = useRef([]);
  const abortRef = useRef(null);
  const typingTimer = useRef(null);
  bindingRef.current = binding;
  threadRef.current = thread;
  const moodsRef = useRef([]);
  moodsRef.current = moods;

  const bind = useCallback((b) => {
    setBinding(b);
    setThread(b.companion);
    setDraftBody(b.body);
    setDraftTitle(b.title || "");
    setRoute(b.route || "libre");
    setAfter(b.after || null);
    setMoods(b.moods || []);
    setError("");
    setMode("escucha");
    return () => {
      abortRef.current?.abort();
      setBinding((cur) => (cur === b ? null : cur));
      setThread([]);
    };
  }, []);

  const typing = useCallback((body, title) => {
    if (typeof body === "string") setDraftBody(body);
    if (typeof title === "string") setDraftTitle(title);
    setMode("espera");
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => setMode((m) => (m === "espera" ? "escucha" : m)), TYPING_IDLE_MS);
  }, []);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  // silent: automatic interventions (tone monitor) never show errors
  const ask = useCallback(async (kind, { silent = false } = {}) => {
    const b = bindingRef.current;
    if (!b || abortRef.current) return false;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setError("");
    setLive({ kind, raw: "" });
    try {
      await b.flush();
      const { title, body, moods, route: r } = b.getDraft();
      const iv = await streamCompanion(b.id, { kind, title, body, moods, route: r },
        (raw) => { setLive({ kind, raw }); clearTimeout(typingTimer.current); setMode("pregunta"); }, ctrl.signal);
      const next = [...threadRef.current, iv];
      setThread(next);
      b.onCompanion(next);
      clearTimeout(typingTimer.current);
      setMode("pregunta");
      if (iv.kind === "care" && isMobile()) setPanelOpen(true);
      if (iv.kind === "giro" && settingsRef.current?.giro_sound) softChime();
    } catch (e) {
      if (e.name !== "AbortError" && !silent) setError("Remanso no pudo responder en este momento. Intenta de nuevo en un rato.");
    } finally {
      abortRef.current = null;
      setLive(null);
    }
    return true;
  }, []);

  const chooseAfter = useCallback((value) => {
    setAfter(value);
    bindingRef.current?.saveAfter(value);
  }, []);

  useEffect(() => {
    api.get("/config/remanso").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const selectQuote = useCallback((quote) => {
    if (isMobile()) {
      setPanelOpen(false);
      setTimeout(() => bindingRef.current?.select(quote), 350);
    } else {
      bindingRef.current?.select(quote);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && bindingRef.current) {
        e.preventDefault();
        ask("ask");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ask]);

  const getThread = useCallback(() => threadRef.current, []);

  const chooseRoute = useCallback((id) => {
    setRoute(id);
    bindingRef.current?.onRoute(id);
  }, []);

  const addStarter = useCallback((text) => {
    const b = bindingRef.current;
    if (!b) return;
    const next = [...threadRef.current, { id: crypto.randomUUID(), kind: "ask", text, quote: null, at: new Date().toISOString() }];
    setThread(next);
    b.onCompanion(next);
    b.persistCompanion(next);
    setMode("pregunta");
  }, []);

  const toggleMood = useCallback((mood) => {
    const cur = moodsRef.current;
    const next = cur.includes(mood) ? cur.filter((m) => m !== mood) : [...cur, mood];
    moodsRef.current = next;
    setMoods(next);
    bindingRef.current?.setMoods(next);
  }, []);

  // info: { view: "guia" | "historia" | "bienvenida", anchor }
  const openInfo = useCallback((view, anchor = null) => { setPanelOpen(false); setInfo({ view, anchor, at: Date.now() }); }, []);
  const closeInfo = useCallback(() => setInfo(null), []);
  const dismissInstall = useCallback(async () => {
    const { data } = await api.post("/install-card/dismiss");
    setSettings(data);
  }, []);
  const draftEmpty = !binding || (!draftTitle.trim() && !draftBody.trim());

  const saveSettings = useCallback(async (patch) => {
    const { data } = await api.put("/settings", patch);
    setSettings(data);
    return data;
  }, []);

  const lastCare = thread[thread.length - 1]?.kind === "care";
  const estado = mode === "espera" ? "espera" : lastCare && !live ? "cuida" : mode;

  return (
    <RemansoContext.Provider value={{
      estado, panelOpen, setPanelOpen, bound: !!binding, thread, draftBody, live, error,
      bind, typing, ask, stop, selectQuote, getThread, route, chooseRoute, addStarter, after, chooseAfter, config,
      moods, syncMoods: setMoods, toggleMood, info, openInfo, closeInfo, settings, saveSettings, dismissInstall, draftEmpty,
    }}>
      {children}
    </RemansoContext.Provider>
  );
};

export const useRemanso = () => useContext(RemansoContext);
